import { useEffect, useState, useCallback, useRef } from "react";
import { adminAPI } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Youtube, Instagram, Facebook, Twitter, Newspaper, Pin, PinOff, Trash2, Plus, ExternalLink, Pencil, X, Upload, Loader2, Info, Bell, BellRing, Users, CheckCircle2 } from "lucide-react";

const PLATFORMS = [
  { id: "youtube", label: "YouTube", icon: Youtube, color: "#FF0000", placeholder: "https://youtube.com/watch?v=..." },
  { id: "instagram", label: "Instagram", icon: Instagram, color: "#E4405F", placeholder: "https://instagram.com/p/..." },
  { id: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2", placeholder: "https://facebook.com/..." },
  { id: "twitter", label: "Twitter/X", icon: Twitter, color: "#000000", placeholder: "https://x.com/..." },
  { id: "news", label: "News", icon: Newspaper, color: "hsl(var(--primary))", placeholder: "https://example.com/article" },
];

const platformConfig = (id) => PLATFORMS.find((p) => p.id === id) || PLATFORMS[4];

const IMAGE_GUIDANCE = {
  youtube: "YouTube thumbnails are detected automatically from the video URL — no image needed.",
  instagram: "Instagram: save/download the post image to your PC, then use \u201cUpload image\u201d below.",
  facebook: "Facebook: save/download the post image to your PC, then use \u201cUpload image\u201d below.",
  twitter: "X/Twitter: right-click the image \u2192 \u201cOpen image in new tab\u201d \u2192 copy that URL into Thumbnail URL, or download it and use \u201cUpload image\u201d.",
  news: "News: paste the article image URL into Thumbnail URL, or download it and use \u201cUpload image\u201d.",
};

const emptyForm = {
  content_type: "youtube",
  title: "",
  description: "",
  source_name: "",
  content_url: "",
  thumbnail_url: "",
  video_id: "",
  pinned: false,
};

function SegmentPicker({ segments, value, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {segments.map((s) => {
        const selected = value === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onChange(s.id)}
            data-testid={`${testIdPrefix}-${s.id}`}
            className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${selected ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-secondary/60"}`}
          >
            <Users className="h-3.5 w-3.5" /> {s.label}
            <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-bold text-foreground/80">{s.count}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function AdminNews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [segments, setSegments] = useState([{ id: "all", label: "all approved members", count: 0 }, { id: "sponsored", label: "sponsored members", count: 0 }]);
  const [createSegment, setCreateSegment] = useState("all");

  // Per-item notify modal
  const [notifyTarget, setNotifyTarget] = useState(null);
  const [notifySegment, setNotifySegment] = useState("all");
  const [notifying, setNotifying] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminAPI
      .getCuratedContent()
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    adminAPI.getNotificationSegments().then((r) => { if (Array.isArray(r.data) && r.data.length) setSegments(r.data); }).catch(() => {});
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setNotifyUsers(false);
    setCreateSegment("all");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleUrlChange = async (url) => {
    setForm((f) => ({ ...f, content_url: url }));
    let detected = null;
    if (url.includes("youtube.com") || url.includes("youtu.be")) detected = "youtube";
    else if (url.includes("instagram.com")) detected = "instagram";
    else if (url.includes("facebook.com") || url.includes("fb.com")) detected = "facebook";
    else if (url.includes("twitter.com") || url.includes("x.com")) detected = "twitter";
    if (detected) setForm((f) => ({ ...f, content_type: detected }));

    if (detected === "youtube") {
      try {
        const res = await adminAPI.parseYouTubeUrl(url);
        if (res.data?.success) {
          setForm((f) => ({ ...f, video_id: res.data.video_id, thumbnail_url: res.data.thumbnail_url || f.thumbnail_url }));
        }
      } catch { /* ignore parse failures */ }
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminAPI.uploadCuratedImage(file);
      setForm((f) => ({ ...f, thumbnail_url: res.data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      content_type: item.content_type || "news",
      title: item.title || "",
      description: item.description || "",
      source_name: item.source_name || "",
      content_url: item.content_url || "",
      thumbnail_url: item.thumbnail_url || "",
      video_id: item.video_id || "",
      pinned: !!item.pinned,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!form.content_url || !form.title || !form.source_name) {
      toast.error("Please fill in URL, Title, and Source Name");
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await adminAPI.updateCuratedContent(editingId, {
          title: form.title,
          description: form.description,
          source_name: form.source_name,
          content_url: form.content_url,
          thumbnail_url: form.thumbnail_url,
          content_type: form.content_type,
          video_id: form.video_id,
          pinned: form.pinned,
        });
        toast.success("Content updated");
      } else {
        await adminAPI.createCuratedContent({ ...form, notify_users: notifyUsers, notify_segment: createSegment });
        if (notifyUsers) {
          const seg = segments.find((s) => s.id === createSegment);
          toast.success(`Content added — notifying ${seg?.label || "members"} now`);
        } else {
          toast.success("Content added to the feed");
        }
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save content");
    } finally {
      setSubmitting(false);
    }
  };

  const togglePin = async (id) => {
    try { await adminAPI.togglePinCuratedContent(id); load(); }
    catch { toast.error("Failed to update pin status"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this content from the feed?")) return;
    try {
      await adminAPI.deleteCuratedContent(id);
      toast.success("Content removed");
      if (editingId === id) resetForm();
      load();
    } catch { toast.error("Failed to remove content"); }
  };

  const openNotify = (item) => {
    setNotifyTarget(item);
    setNotifySegment("all");
  };

  const sendNotify = async (force = false) => {
    if (!notifyTarget) return;
    setNotifying(true);
    try {
      await adminAPI.notifyCuratedContent(notifyTarget.id, notifySegment, force);
      const seg = segments.find((s) => s.id === notifySegment);
      toast.success(`Notifying ${seg?.label || "members"} now`);
      setNotifyTarget(null);
      // Poll twice so the background broadcast's reach count shows up even on slow envs.
      setTimeout(load, 1200);
      setTimeout(load, 3000);
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm("This item has already been sent to users. Re-send anyway?")) {
          await sendNotify(true);
          return;
        }
      } else {
        toast.error(e.response?.data?.detail || "Failed to notify users");
      }
    } finally {
      setNotifying(false);
    }
  };

  const active = items.filter((i) => i.is_active);
  const cfg = platformConfig(form.content_type);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold">News &amp; Hot Content</h2>
        <p className="text-sm text-muted-foreground">Share from YouTube, Instagram, Facebook, Twitter/X, or news sources. Appears on the Home feed.</p>
      </div>

      {/* Add / edit form */}
      <Card className="border-border bg-card p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{editingId ? "Edit content" : "Add content"}</h3>
          {editingId && (
            <Button variant="ghost" size="sm" onClick={resetForm} data-testid="admin-news-cancel-edit">
              <X className="mr-1 h-4 w-4" /> Cancel edit
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {PLATFORMS.map((p) => {
            const Icon = p.icon;
            const selected = form.content_type === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setForm((f) => ({ ...f, content_type: p.id }))}
                data-testid={`admin-news-platform-${p.id}`}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${selected ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-secondary/60"}`}
              >
                <Icon className="h-4 w-4" style={{ color: p.color }} /> {p.label}
              </button>
            );
          })}
        </div>

        {/* Image guidance */}
        <div className="flex items-start gap-2 rounded-md border border-border bg-secondary/40 p-3 text-xs text-muted-foreground" data-testid="admin-news-guidance">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>{IMAGE_GUIDANCE[form.content_type]}</span>
        </div>

        <div className="space-y-2">
          <Label>Content URL</Label>
          <Input
            value={form.content_url}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder={cfg.placeholder}
            data-testid="admin-news-url"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Headline" data-testid="admin-news-title" />
          </div>
          <div className="space-y-2">
            <Label>Source name</Label>
            <Input value={form.source_name} onChange={(e) => setForm((f) => ({ ...f, source_name: e.target.value }))} placeholder="e.g. Driver61, Autosport" data-testid="admin-news-source" />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Description (optional)</Label>
          <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Short summary" rows={2} data-testid="admin-news-description" />
        </div>

        <div className="space-y-2">
          <Label>Thumbnail URL</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input className="flex-1" value={form.thumbnail_url} onChange={(e) => setForm((f) => ({ ...f, thumbnail_url: e.target.value }))} placeholder="https://... or upload an image" data-testid="admin-news-thumbnail" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="admin-news-file" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="admin-news-upload-btn">
              {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} Upload image
            </Button>
          </div>
        </div>

        {form.thumbnail_url ? (
          <img src={form.thumbnail_url} alt="preview" className="h-32 w-full rounded-md object-cover sm:w-64" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : null}

        {/* Pinned toggle (available when editing) */}
        {editingId && (
          <div className="flex items-center gap-3 rounded-md border border-border bg-secondary/30 p-3" data-testid="admin-news-pinned-row">
            <Switch checked={form.pinned} onCheckedChange={(v) => setForm((f) => ({ ...f, pinned: !!v }))} data-testid="admin-news-pinned-toggle" />
            <div className="flex items-center gap-1.5 text-sm">
              <Pin className="h-4 w-4 text-primary" />
              <span>Pinned{form.pinned ? " — shows at the top of the feed" : ""}</span>
            </div>
          </div>
        )}

        {!editingId && (
          <div className="space-y-2 rounded-md border border-border bg-secondary/30 p-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm" data-testid="admin-news-notify-label">
              <Checkbox checked={notifyUsers} onCheckedChange={(v) => setNotifyUsers(!!v)} data-testid="admin-news-notify" />
              <Bell className="h-4 w-4 text-primary" />
              <span>Notify users about this new content</span>
            </label>
            {notifyUsers && (
              <div className="pl-7">
                <p className="mb-1.5 text-xs text-muted-foreground">Send to:</p>
                <SegmentPicker segments={segments} value={createSegment} onChange={setCreateSegment} testIdPrefix="admin-news-create-segment" />
              </div>
            )}
          </div>
        )}

        <Button onClick={handleSubmit} disabled={submitting} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="admin-news-submit">
          {editingId ? <Pencil className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
          {submitting ? "Saving..." : editingId ? "Update content" : "Add to feed"}
        </Button>
      </Card>

      {/* Current feed */}
      <div>
        <h3 className="mb-3 font-semibold">Current Feed ({active.length})</h3>
        {loading ? (
          <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}</div>
        ) : active.length === 0 ? (
          <Card className="border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">No content yet. Add your first item above.</Card>
        ) : (
          <div className="space-y-3">
            {active.map((item) => {
              const c = platformConfig(item.content_type);
              const Icon = c.icon;
              return (
                <Card key={item.id} className="flex items-start gap-3 border-border bg-card p-3" data-testid={`admin-news-item-${item.id}`}>
                  {item.thumbnail_url ? (
                    <img src={item.thumbnail_url} alt="" className="h-16 w-24 shrink-0 rounded object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded bg-secondary/60">
                      <Icon className="h-6 w-6" style={{ color: c.color }} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: c.color }} />
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{c.label}</span>
                      {item.pinned && <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">Pinned</span>}
                      {item.notified && (
                        <span className="inline-flex items-center gap-1 rounded bg-green-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-green-500" data-testid={`admin-news-reach-${item.id}`}>
                          <CheckCircle2 className="h-3 w-3" /> Sent · {item.notified_count || 0}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 truncate font-medium">{item.title}</p>
                    <p className="truncate text-xs text-muted-foreground">{item.source_name}</p>
                    <a href={item.content_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      <ExternalLink className="h-3 w-3" /> Open
                    </a>
                  </div>
                  <div className="flex shrink-0 flex-col gap-2">
                    <Button variant="outline" size="sm" onClick={() => openNotify(item)} data-testid={`admin-news-notify-btn-${item.id}`} title={item.notified ? "Re-send notification" : "Notify users"}>
                      {item.notified ? <BellRing className="h-4 w-4 text-green-500" /> : <Bell className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => startEdit(item)} data-testid={`admin-news-edit-${item.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => togglePin(item.id)} data-testid={`admin-news-pin-${item.id}`}>
                      {item.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => remove(item.id)} data-testid={`admin-news-delete-${item.id}`} className="text-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Per-item notify modal */}
      {notifyTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !notifying && setNotifyTarget(null)} data-testid="admin-news-notify-modal">
          <Card className="w-full max-w-md border-border bg-card p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">Notify users</h3>
              <button onClick={() => !notifying && setNotifyTarget(null)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>
            <p className="mb-1 truncate text-sm font-medium">{notifyTarget.title}</p>
            {notifyTarget.notified && (
              <p className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-300" data-testid="admin-news-already-sent">
                Already sent to {notifyTarget.notified_count || 0} member(s){notifyTarget.notified_segment ? ` (${notifyTarget.notified_segment})` : ""}. Sending again will notify them once more.
              </p>
            )}
            <p className="mb-1.5 text-xs text-muted-foreground">Send to:</p>
            <SegmentPicker segments={segments} value={notifySegment} onChange={setNotifySegment} testIdPrefix="admin-news-modal-segment" />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNotifyTarget(null)} disabled={notifying}>Cancel</Button>
              <Button onClick={() => sendNotify(false)} disabled={notifying} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="admin-news-modal-send">
                {notifying ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Bell className="mr-1 h-4 w-4" />}
                {notifyTarget.notified ? "Re-send" : "Send notification"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
